import React, { FormEvent, useEffect, useState } from "react"
import { Button, Col, Form, Modal, Row } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import ScaleLoader from "react-spinners/ScaleLoader"
import api from "../../api"
import { useAppSelector } from "../../app/hooks"
import { RootState } from "../../store"

const UpdateMAWBStatusModel = ({
    show,
    setShow,
    data,
    setData,
    updateMAWBStatus
}: {
    show: string
    setShow: Function
    data: Record<string, any>
    setData: Function
    updateMAWBStatus: Function
}) => {
    const { t } = useTranslation()

    //const [isDisabled, setIsDisabled] = useState(false)
    const { pallets } = useAppSelector((state: RootState) => state.linehaul)
    const { countryList } = useAppSelector((state: RootState) => state.customers)
    const [loading, setLoading] = useState(false)
    const [errors, setErrors] = useState([] as string[])
    const [statusesList, setStatusesList] = useState([] as Record<string, any>[])

    let fields = [
        { name: "date", title: "Date and Time", required: true, colSpan: 12 },
        { name: "status", title: "Status", required: true, colSpan: 12 },
        { name: "country", title: "Country", required: true, colSpan: 12 }
    ]

    const loadStatuses = async () => {
        try {
            let { data: resData } = await api.linehaul.LineHaulStatuses({})
            if (resData?.status) {
                setStatusesList([...resData?.payload])
            }
        } catch (error: any) {
            console.log(error?.message)
        }
    }

    useEffect(() => {
        setLoading(true)
        loadStatuses()
        setLoading(false)
    }, [])

    const handelInputChanges = (e: FormEvent<HTMLInputElement>, required = false) => {
        let value = e?.currentTarget.value as any
        let name = e?.currentTarget.name
        if (name === "status") {
            let selectedStatus = statusesList.find((s: Record<string, any>) => s?.id === parseInt(value))
            if (selectedStatus?.id) {
                value = {
                    id: selectedStatus?.id,
                    detail: selectedStatus?.name
                }
            } else {
                value = null
            }
        }
        if (required && (!value || value?.length === 0 || parseFloat(value) === 0)) {
            setErrors([...errors?.filter((e) => e !== name), name])
        } else {
            setErrors([...errors?.filter((e) => e !== name)])
        }
        setData({ ...data, [name]: value })
    }

    const checkRequiredFields = (data: Record<string, any>) => {
        let safe = true
        let fieldsWithIssues = [] as string[]
        fields.forEach((f) => {
            if (
                f.required &&
                (!data[`${f.name}`] || data[`${f.name}`]?.length === 0 || parseFloat(data[`${f.name}`]) === 0)
            ) {
                safe = false
                fieldsWithIssues.push(f.name)
            }
        })
        return {
            status: safe,
            issues: fieldsWithIssues
        }
    }

    const handleSubmit = async () => {
        let reqData = { ...data }
        let validity = checkRequiredFields(reqData)
        setLoading(true)
        if (validity.status) {
            reqData["date"] = new Date(reqData["date"]).toUTCString()
            await updateMAWBStatus(reqData)
        } else {
            setErrors([
                ...errors?.filter((e) => !(validity.issues || [])?.includes(e)),
                ...(validity?.issues || [])
            ])
        }
        setLoading(false)
    }

    return loading ? (
        <div className="loader row justify-content-center">
            <ScaleLoader color="#15397F" width={4} height={40} />
        </div>
    ) : (
        <Modal
            size="lg"
            show={["updateMAWBStatus"].includes(show)}
            onHide={() => {
                setShow("")
                setData({})
            }}
            onClose={() => {
                setShow("")
                setData({})
            }}
            backdrop="static"
            // contentClassName={"gray-bg"}
            keyboard={false}>
            <Modal.Header closeButton>
                <Modal.Title className="text-uppercase mx-2">
                    {t("MASTER AWB")}&nbsp;{`# ${data.mawb}`}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="modal-gray-bg">
                <div className="main-container">
                    <Row>
                        <Col>
                            <Form className="font-13 my-3">
                                <Row className="px-1 justify-content-center align-items-start my-2">
                                    <Col
                                        lg={12}
                                        sm={12}
                                        className="d-flex justify-content-start align-items-start">
                                        <Form.Group className="mb-3 mx-2 w-100">
                                            <Form.Label htmlFor="date" className="required">
                                                {t("Date and time")}
                                            </Form.Label>
                                            <Form.Control
                                                value={data?.date}
                                                type={"datetime-local"}
                                                name={"date"}
                                                autoComplete="off"
                                                onChange={(e: any) => handelInputChanges(e)}
                                                isInvalid={!!errors?.includes("date")}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {t(`This field is required`)}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col
                                        lg={12}
                                        sm={12}
                                        className="d-flex justify-content-start align-items-start">
                                        <Form.Group className="mb-3 mx-2 w-100">
                                            <Form.Label htmlFor="country" className="required">
                                                {t("Country")}
                                            </Form.Label>
                                            <Form.Select
                                                value={data?.country}
                                                className="text-capitalize font-13"
                                                name={"country"}
                                                placeholder={t("Select country...")}
                                                onChange={(e: any) => handelInputChanges(e, true)}
                                                isInvalid={!!errors?.includes("country")}>
                                                <option placeholder="Select country">Select country</option>
                                                {countryList?.map((e: Record<string, any>) => (
                                                    <option value={e.iso2_code} key={`lh-${e.iso2_code}`}>
                                                        {e.name}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                            <Form.Control.Feedback type="invalid">
                                                {t(`This field is required`)}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col
                                        lg={12}
                                        sm={12}
                                        className="d-flex justify-content-start align-items-start">
                                        <Form.Group className="mb-3 mx-2 w-100">
                                            <Form.Label htmlFor="status" className="required">
                                                {t("Status")}
                                            </Form.Label>
                                            <Form.Select
                                                value={data?.status?.id}
                                                className="text-capitalize font-13"
                                                name={"status"}
                                                placeholder={t("Select status...")}
                                                onChange={(e: any) => handelInputChanges(e, true)}
                                                isInvalid={!!errors?.includes("status")}>
                                                <option placeholder="Select status">Select status</option>
                                                {statusesList.map((e: Record<string, any>) => (
                                                    <option value={e.id} key={e.name?.replaceAll(" ", "_")}>
                                                        {e?.name}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                            <Form.Control.Feedback type="invalid">
                                                {t(`This field is required`)}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Form>
                        </Col>
                    </Row>
                    <Row className="px-1">
                        <Col className="d-flex align-items-center justify-content-end">
                            <Button
                                disabled={false}
                                className="float-end mb-2 gray-bg px-3 mx2"
                                onClick={() => {
                                    setShow("")
                                    setData({})
                                }}>
                                {t("Cancel")}
                            </Button>
                            <Button className="float-end mb-2 primary-bg px-3 mx-2" onClick={handleSubmit}>
                                {loading ? <div className="spinner-border text-light"></div> : t("Save")}
                            </Button>
                        </Col>
                    </Row>
                </div>
            </Modal.Body>
        </Modal>
    )
}

export default UpdateMAWBStatusModel
